import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getActivity, type ActivityItem } from '../api';
import PageHeader from '../components/PageHeader';

const KIND_META: Record<string, { label: string; cls: string }> = {
  bet_created:  { label: 'BET',     cls: 'bg-pitch-500/15 text-pitch-400 ring-pitch-500/30' },
  bet_settled:  { label: 'BET FT',  cls: 'bg-amber-500/15 text-amber-400 ring-amber-500/30' },
  comment:      { label: 'COMMENT', cls: 'bg-cyan-500/15 text-cyan-400 ring-cyan-500/30' },
  prediction:   { label: 'PICK',    cls: 'bg-purple-500/15 text-purple-400 ring-purple-500/30' },
  news:         { label: 'NEWS',    cls: 'bg-blue-500/15 text-blue-400 ring-blue-500/30' },
  match_kickoff:{ label: 'KICKOFF', cls: 'bg-sky-500/15 text-sky-400 ring-sky-500/30' },
  match_ended:  { label: 'FT',      cls: 'bg-sky-500/15 text-sky-400 ring-sky-500/30' },
};

export default function Activity() {
  const { data, isLoading } = useQuery({ queryKey: ['activity'], queryFn: getActivity, refetchInterval: 30_000 });
  return (
    <>
      <PageHeader title={'Activity'} subtitle="Everything happening across the league." />
      <div className="px-4 lg:px-0 space-y-2">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="card h-14 animate-pulse bg-ink-900/60" />)
        ) : (data?.items.length ?? 0) === 0 ? (
          <div className="card p-6 text-center text-dim text-sm">Nothing yet.</div>
        ) : (
          data!.items.map((it) => <Row key={it.id} item={it} />)
        )}
      </div>
    </>
  );
}

function Row({ item }: { item: ActivityItem }) {
  const meta = KIND_META[item.kind] ?? { label: item.kind.toUpperCase(), cls: 'bg-ink-700 text-dim ring-ink-600' };
  const inner = (
    <div className="card p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase ring-1 ${meta.cls}`}>
          {meta.label}
        </span>
        <span className="text-[10px] text-dim">{relative(item.created_at)}</span>
      </div>
      <div className="text-sm leading-tight">{item.title}</div>
      {item.body && <div className="text-xs text-dim mt-0.5 line-clamp-2">{item.body}</div>}
    </div>
  );
  return item.link ? <Link to={item.link} className="block">{inner}</Link> : inner;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
